import React, { useState, Fragment, createContext, useContext } from 'react';
import { NT, NI, NMButton } from './NaomiTheme';
import { MMenu } from './NaomiUi';
import { TypeIcon } from './NaomiBuilders';

interface OutlineTreeProps {
  sdm: any;
  selectedOid: string | null;
  onSelect: (oid: string | null) => void;
  onAddQuestion: (groupOid?: string) => void;
  onAddSubgroup: (parentOid: string) => void;
  onAddTopGroup: () => void;
  onRenameGroup: (groupOid: string) => void;
  onDeleteGroup: (groupOid: string) => void;
  onAddBranchToGroup: (groupOid: string) => void;
  onClearGroupBranch: (groupOid: string) => void;
  onSetGroupBanner: (groupOid: string) => void;
  onDuplicate: (groupOid: string, itemOid: string) => void;
  onDelete: (groupOid: string, itemOid: string) => void;
  onMoveUp: (groupOid: string, oid: string) => void;
  onMoveDown: (groupOid: string, oid: string) => void;
  onReorder: (fromGroupOid: string, fromItemOid: string, toGroupOid: string, toIdx: number) => void;
  onReorderGroup: (fromParentKey: string, fromGroupOid: string, toParentKey: string, toIdx: number) => void;
  onConfigureRepeating: (groupOid: string) => void;
  onTradeLogo?: () => void;
  tradeLogo?: string;
}

interface GroupNodeContextValue {
  itemByOid: Record<string, any>;
  condByOid: Record<string, any>;
  methByOid: Record<string, any>;
  toggle: (oid: string) => void;
  onSelect: (oid: string | null) => void;
  onAddQuestion: (groupOid?: string) => void;
  onAddSubgroup: (parentOid: string) => void;
  onRenameGroup: (groupOid: string) => void;
  onDeleteGroup: (groupOid: string) => void;
  onAddBranchToGroup: (groupOid: string) => void;
  onClearGroupBranch: (groupOid: string) => void;
  onDuplicate: (groupOid: string, itemOid: string) => void;
  onDelete: (groupOid: string, itemOid: string) => void;
  onMoveUp: (groupOid: string, oid: string) => void;
  onMoveDown: (groupOid: string, oid: string) => void;
  setMenu: (menu: any) => void;
  onDragStart: (groupOid: string, itemOid: string, idx: number) => (e: React.DragEvent) => void;
  onDragOver: (groupOid: string, idx: number) => (e: React.DragEvent) => void;
  onDrop: (groupOid: string, idx: number) => (e: React.DragEvent) => void;
  onSectionDragStart: ((parentKey: string, groupOid: string, idx: number) => (e: React.DragEvent) => void) | null;
  onSectionDrop: ((parentKey: string, idx: number) => (e: React.DragEvent) => void) | null;
  onSectionDragEnd: (() => void) | null;
}

const GroupNodeContext = createContext<GroupNodeContextValue>(null!);

function OutlineTree({ sdm, selectedOid, onSelect, onAddQuestion, onAddSubgroup, onAddTopGroup, onRenameGroup, onDeleteGroup, onAddBranchToGroup, onClearGroupBranch, onSetGroupBanner,
  onDuplicate, onDelete, onMoveUp, onMoveDown, onReorder, onReorderGroup, onConfigureRepeating, onTradeLogo, tradeLogo }: OutlineTreeProps) {
  const tv = sdm.TemplateVersion[0];
  const itemByOid  = Object.fromEntries(tv.ItemDef.map(i => [i.OID, i]));
  const groupByOid = Object.fromEntries(tv.ItemGroupDef.map(g => [g.OID, g]));
  const condByOid  = Object.fromEntries(tv.ConditionDef.map(c => [c.OID, c]));
  const methByOid  = Object.fromEntries(tv.MethodDef.map(m => [m.OID, m]));

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [menu, setMenu] = useState<
    | { kind: 'item'; anchor: HTMLElement; payload: { groupOid: string; itemOid: string; idx: number; refsLen: number } }
    | { kind: 'group'; anchor: HTMLElement; payload: { groupOid: string; hasBranch: boolean; hasBanner: boolean; isRepeating: boolean; isSubgroup: boolean } }
    | null
  >(null);
  const [drag, setDrag] = useState<{ groupOid: string; itemOid: string; idx: number } | null>(null);
  const [sectionDrag, setSectionDrag] = useState<{ parentKey: string; groupOid: string; idx: number } | null>(null);
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const matchItem = (item) => !q || (item.Question.TranslatedText.text || '').toLowerCase().includes(q) ||
    (item.Name || '').toLowerCase().includes(q);
  const matchGroup = (group) => !q || (group.Name || '').toLowerCase().includes(q) ||
    group.ItemRef.some(r => itemByOid[r.ItemOID] && matchItem(itemByOid[r.ItemOID])) ||
    (group.Subgroups || []).some(sg => matchGroup(sg));

  const toggle = oid => setCollapsed(c => ({ ...c, [oid]: !c[oid] }));

  const onDragStart = (groupOid, itemOid, idx) => (e) => {
    setDrag({ groupOid, itemOid, idx });
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (groupOid, idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (groupOid, idx) => (e) => {
    e.preventDefault();
    if (drag) onReorder(drag.groupOid, drag.itemOid, groupOid, idx);
    setDrag(null);
  };

  const onSectionDragStart = (parentKey, groupOid, idx) => (e) => {
    setSectionDrag({ parentKey, groupOid, idx });
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
  };
  const onSectionDragOver = (e) => {
    if (!sectionDrag) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onSectionDragEnd = () => setSectionDrag(null);

  const onSectionDrop = (parentKey, idx) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (sectionDrag && onReorderGroup) {
      const fromIsTopLevel = tv.FormDef.some(f => f.OID === sectionDrag.parentKey);
      const toIsTopLevel   = tv.FormDef.some(f => f.OID === parentKey);
      if (fromIsTopLevel === toIsTopLevel) {
        onReorderGroup(sectionDrag.parentKey, sectionDrag.groupOid, parentKey, idx);
      }
    }
    setSectionDrag(null);
  };

  const ctx: GroupNodeContextValue = {
    itemByOid, condByOid, methByOid,
    toggle, onSelect, onAddQuestion, onAddSubgroup,
    onRenameGroup, onDeleteGroup, onAddBranchToGroup, onClearGroupBranch,
    onDuplicate, onDelete, onMoveUp, onMoveDown,
    setMenu, onDragStart, onDragOver, onDrop, onSectionDragStart, onSectionDrop, onSectionDragEnd,
  };

  return (
    <GroupNodeContext.Provider value={ctx}>
      <div className="naomi-scroll" style={{ height: '100%', overflow: 'auto' }}>
        <div style={{ padding: '14px 14px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {onAddTopGroup && <NMButton id="naomi-outline-button-add-section" size="sm" variant="outlined" color="neutral" icon={<NI.Group/>} onClick={() => onAddTopGroup()}>Section</NMButton>}
            <NMButton id="naomi-outline-button-add-question" size="sm" variant="soft" icon={<NI.Plus/>} onClick={() => onAddQuestion()}>Question</NMButton>
            {onTradeLogo && (
              <NMButton id="naomi-outline-button-trade-logo" size="sm" variant="outlined" color="neutral"
                icon={tradeLogo
                  ? <img src={tradeLogo} alt="" style={{ width: 14, height: 14, objectFit: 'contain', borderRadius: 2 }}/>
                  : <NI.Camera/>}
                onClick={onTradeLogo}>Logo</NMButton>
            )}
          </div>
        </div>

        <div style={{ padding: '0 12px 8px' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: NT.ink4 }}><NI.Search/></span>
            <input id="naomi-outline-input-search" className="nm-input" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search questions or sections…"
              style={{ width: '100%', height: 30, padding: '0 26px 0 28px', fontSize: 12.5,
                background: NT.surfaceAlt, border: `1px solid ${NT.line}`, borderRadius: 6 }}/>
            {search && <button onClick={() => setSearch('')}
              className="nm-icon-btn"
              style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                width: 22, height: 22, color: NT.ink4 }}><NI.Close/></button>}
          </div>
          {q && (
            <div style={{ fontSize: 11, color: NT.ink3, marginTop: 4, paddingLeft: 4 }}>
              {(() => {
                const cnt = tv.ItemDef.filter(matchItem).length;
                return cnt === 0 ? 'No matches' : `${cnt} match${cnt === 1 ? '' : 'es'}`;
              })()}
            </div>
          )}
        </div>

        <div style={{ padding: '4px 6px 80px' }}>
          {tv.EventDef[0].FormRef.map(fr => {
            const form = tv.FormDef.find(f => f.OID === fr.FormOID);
            if (!form) return null;
            const isCol = collapsed[form.OID];
            return (
              <div key={form.OID} style={{ marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '4px 8px',
                  fontSize: 11, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .5 }}>
                  <button onClick={() => toggle(form.OID)} className="nm-icon-btn" style={{ width: 20, height: 20 }}>
                    <NI.Chev d={isCol ? 'right' : 'down'}/>
                  </button>
                  <span style={{ flex: 1, marginLeft: 2 }}>{form.Name}</span>
                </div>

                {!isCol && form.ItemGroupRef.map((gr, idx) => {
                  const group = groupByOid[gr.ItemGroupOID];
                  if (!group) return null;
                  if (q && !matchGroup(group)) return null;
                  return (
                    <div key={group.OID}
                      onDragOver={onSectionDragOver}
                      onDrop={(e) => onSectionDrop(form.OID, idx + 1)(e)}>
                      <GroupNode group={group} groupRef={gr} depth={1}
                        collapsed={collapsed}
                        selectedOid={selectedOid}
                        searchTerm={q} matchItem={matchItem}
                        drag={drag}
                        sectionDrag={sectionDrag}
                        formOid={form.OID}
                        ownIdx={idx}/>
                    </div>
                  );
                })}
                {!isCol && (
                  <div style={{ height: 8 }}
                    onDragOver={onSectionDragOver}
                    onDrop={onSectionDrop(form.OID, form.ItemGroupRef.length)}/>
                )}
              </div>
            );
          })}
        </div>

        {menu && menu.kind === 'item' && (
          <MMenu anchor={menu.anchor} onClose={() => setMenu(null)} items={[
            { label: 'Edit', icon: <NI.Edit/>, onClick: () => onSelect(menu.payload.itemOid) },
            { label: 'Move up', icon: <NI.Chev d="up"/>, disabled: menu.payload.idx===0,
              onClick: () => onMoveUp(menu.payload.groupOid, menu.payload.itemOid) },
            { label: 'Move down', icon: <NI.Chev d="down"/>, disabled: menu.payload.idx===menu.payload.refsLen-1,
              onClick: () => onMoveDown(menu.payload.groupOid, menu.payload.itemOid) },
            { divider: true },
            { label: 'Delete', icon: <NI.Trash/>, danger: true,
              onClick: () => onDelete(menu.payload.groupOid, menu.payload.itemOid) },
          ]}/>
        )}
        {menu && menu.kind === 'group' && (
          <MMenu anchor={menu.anchor} onClose={() => setMenu(null)} items={[
            { label: 'Rename', icon: <NI.Edit/>, onClick: () => onRenameGroup(menu.payload.groupOid) },
            { label: 'Add question', icon: <NI.Plus/>, onClick: () => onAddQuestion(menu.payload.groupOid) },
            ...(!menu.payload.isSubgroup ? [{ label: 'Add subgroup', icon: <NI.Group/>, onClick: () => onAddSubgroup(menu.payload.groupOid) }] : []),
            { divider: true },
            { label: menu.payload.hasBanner ? 'Edit image' : 'Add image', icon: <NI.Eye/>,
              onClick: () => onSetGroupBanner && onSetGroupBanner(menu.payload.groupOid) },
            { divider: true },
            { label: menu.payload.hasBranch ? 'Edit branching' : 'Add branching', icon: <NI.Branch/>,
              onClick: () => onAddBranchToGroup(menu.payload.groupOid) },
            ...(menu.payload.hasBranch ? [{ label: 'Remove branching', icon: <NI.Trash/>, danger: true,
              onClick: () => onClearGroupBranch(menu.payload.groupOid) }] : []),
            { divider: true },
            { label: menu.payload.isRepeating ? 'Configure repeating' : 'Set as repeating', icon: <NI.Repeat/>,
              onClick: () => onConfigureRepeating && onConfigureRepeating(menu.payload.groupOid) },
            { divider: true },
            { label: 'Delete section', icon: <NI.Trash/>, danger: true,
              onClick: () => onDeleteGroup(menu.payload.groupOid) },
          ]}/>
        )}
      </div>
    </GroupNodeContext.Provider>
  );
}

const extractRepeatTriggerOid = (meth: any): string | null => {
  if (!meth || meth.kind !== 'repeat') return null;
  const m = (meth.FormalExpression?.text || '').match(/!\(([^\s:)]+)/);
  return m ? m[1].trim() : null;
};

interface GroupNodeProps {
  group: any;
  groupRef: any;
  depth: number;
  collapsed: Record<string, boolean>;
  selectedOid: string | null;
  drag: { groupOid: string; itemOid: string; idx: number } | null;
  searchTerm: string;
  matchItem: (item: any) => boolean;
  sectionDrag: { parentKey: string; groupOid: string; idx: number } | null;
  formOid: string | null;
  ownIdx: number;
}

function GroupNode({ group, groupRef, depth, collapsed, selectedOid, drag, searchTerm, matchItem, sectionDrag, formOid, ownIdx }: GroupNodeProps) {
  const { itemByOid, condByOid, methByOid, toggle, onSelect, onAddQuestion, onAddSubgroup,
    setMenu, onDragStart, onDragOver, onDrop, onSectionDragStart, onSectionDrop, onSectionDragEnd } = useContext(GroupNodeContext);

  const isGCol = collapsed[group.OID];
  const groupRepeat = groupRef?.MethodOID ? methByOid[groupRef.MethodOID] : null;
  const groupBranch = groupRef?.CollectionExceptionConditionOID
    ? condByOid[groupRef.CollectionExceptionConditionOID] : null;
  const inlineBranch = group.ConditionOID ? condByOid[group.ConditionOID] : null;
  const branch = groupBranch || inlineBranch;

  const subgroupByTrigger: Record<string, any[]> = {};
  const unpairedSubgroups: any[] = [];
  for (const sg of (group.Subgroups || [])) {
    if (sg.Repeating !== 'Yes') { unpairedSubgroups.push(sg); continue; }
    const sgMeth = sg.MethodOID ? methByOid[sg.MethodOID] : null;
    const trigOid = extractRepeatTriggerOid(sgMeth);
    if (trigOid && group.ItemRef.some((r: any) => r.ItemOID === trigOid)) {
      if (!subgroupByTrigger[trigOid]) subgroupByTrigger[trigOid] = [];
      subgroupByTrigger[trigOid].push(sg);
    } else {
      unpairedSubgroups.push(sg);
    }
  }

  const indent = 8 + depth * 12;

  return (
    <div>
      <div className="nm-row" style={{ display: 'flex', alignItems: 'center',
        padding: `5px 8px 5px ${indent}px`, borderRadius: 5, cursor: 'pointer',
        ...(branch ? { boxShadow: `inset 2px 0 0 ${NT.logic}` } : {}),
        ...(sectionDrag?.groupOid === group.OID ? { opacity: 0.4 } : {}) }}
        draggable
        onDragStart={onSectionDragStart ? onSectionDragStart(formOid || groupRef?.ItemGroupOID || group.OID, group.OID, ownIdx) : undefined}
        onDragEnd={onSectionDragEnd ?? undefined}
        onDragOver={sectionDrag && sectionDrag.groupOid !== group.OID ? (e: React.DragEvent<HTMLDivElement>) => {          e.preventDefault();
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          const isBottom = e.clientY > rect.top + rect.height / 2;
          e.currentTarget.style.boxShadow = isBottom ? `inset 0 -2px 0 ${NT.primary}` : `inset 0 2px 0 ${NT.primary}`;
        } : undefined}
        onDragLeave={groupRef === null && sectionDrag ? (e: React.DragEvent<HTMLDivElement>) => {
          e.currentTarget.style.boxShadow = branch ? `inset 2px 0 0 ${NT.logic}` : '';
        } : undefined}
        onDrop={sectionDrag && sectionDrag.groupOid !== group.OID && onSectionDrop ? (e: React.DragEvent<HTMLDivElement>) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          const isBottom = e.clientY > rect.top + rect.height / 2;
          const targetIdx = isBottom ? ownIdx + 1 : ownIdx;
          onSectionDrop(formOid!, targetIdx)(e);
        } : undefined}
        onClick={() => toggle(group.OID)}>
        <span style={{ color: NT.ink4, cursor: 'grab', display: 'inline-flex', marginRight: 4 }}><NI.Drag/></span>
        <span id={`naomi-outline-iconbutton-group-toggle-${group.OID}`} className="nm-icon-btn" style={{ width: 18, height: 18, color: NT.ink3 }}>
          <NI.Chev d={isGCol ? 'right' : 'down'}/>
        </span>
        <span style={{ color: NT.ink4, marginRight: 6 }}><NI.Group/></span>
        <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: NT.ink2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.Name}</span>
        <span style={{ fontSize: 10.5, color: NT.ink4, fontWeight: 500 }}>{group.ItemRef.length}</span>
        {group.Repeating === 'Yes' && (
          <span style={{ marginLeft: 4, color: NT.warn, display: 'inline-flex' }} title="Repeating section">
            <NI.Repeat/>
          </span>
        )}
        {branch && (
          <span style={{ marginLeft: 4, color: NT.logic, display: 'inline-flex' }} title={`Show when: ${branch.humanReadable}`}>
            <NI.Branch/>
          </span>
        )}
        <button id={`naomi-outline-iconbutton-group-settings-${group.OID}`} className="nm-icon-btn" onClick={e => { e.stopPropagation(); setMenu({ anchor: e.currentTarget,
          kind: 'group', payload: { groupOid: group.OID, hasBranch: !!branch, hasBanner: !!group.HeaderImage, isRepeating: group.Repeating === 'Yes', isSubgroup: depth >= 3 } }); }}
          style={{ width: 22, height: 22, marginLeft: 2 }}>
          <NI.Settings/>
        </button>
      </div>

      {!isGCol && (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', left: indent + 10, top: 0, bottom: 0, width: 1.5,
            background: branch ? NT.logic + '40' : NT.line, borderRadius: 1 }}/>

          {group.ItemRef.map((ref, idx) => {
            const it = itemByOid[ref.ItemOID];
            if (!it) return null;
            if (searchTerm && matchItem && !matchItem(it)) return null;
            const sel = selectedOid === it.OID;
            const cond = ref.CollectionExceptionConditionOID ? condByOid[ref.CollectionExceptionConditionOID] : null;
            const meth = ref.MethodOID ? methByOid[ref.MethodOID] : null;
            const isDragging = drag?.itemOid === it.OID;
            const pairedSubs = subgroupByTrigger[ref.ItemOID] || [];
            return (
              <Fragment key={ref.ItemOID + '@' + idx}>
                <div
                  draggable
                  onDragStart={onDragStart(group.OID, it.OID, idx)}
                  onDragOver={onDragOver(group.OID, idx)}
                  onDrop={onDrop(group.OID, idx)}
                  onClick={() => onSelect(it.OID)}
                  className="nm-row"
                  style={{ display: 'flex', alignItems: 'center', gap: 6,
                    padding: `5px 8px 5px ${indent + 22}px`, borderRadius: 5, cursor: 'pointer',
                    background: sel ? NT.primarySoft : 'transparent',
                    boxShadow: sel ? `inset 2px 0 0 ${NT.primary}` : pairedSubs.length > 0 ? `inset 2px 0 0 ${NT.warn}` : 'none',
                    opacity: isDragging ? .4 : 1 }}>
                  <span style={{ color: NT.ink4, cursor: 'grab', display: 'inline-flex' }}><NI.Drag/></span>
                  <span style={{ color: sel ? NT.primary : NT.ink4 }}><TypeIcon type={it.DataType}/></span>
                  <span style={{ flex: 1, fontSize: 12.5, color: sel ? NT.primary2 : NT.ink2,
                    fontWeight: sel ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.Question.TranslatedText.text}
                  </span>
                  {ref.Mandatory === 'Yes' && <span style={{ color: NT.danger, fontSize: 13, lineHeight: 1 }}>*</span>}
                  {cond && <span style={{ color: NT.logic, display: 'inline-flex' }} title={`Show when: ${cond.humanReadable}`}><NI.Branch/></span>}
                  {meth && <span style={{ color: meth.kind==='repeat'?NT.warn:NT.calc, display: 'inline-flex' }} title={meth.humanReadable}>
                    {meth.kind==='repeat' ? <NI.Repeat/> : <NI.Calc/>}
                  </span>}
                  <button id={`naomi-outline-iconbutton-item-settings-${it.OID}`} className="nm-icon-btn" onClick={e => { e.stopPropagation(); setMenu({ anchor: e.currentTarget,
                    kind: 'item', payload: { groupOid: group.OID, itemOid: it.OID, idx, refsLen: group.ItemRef.length } }); }}
                    style={{ width: 22, height: 22, opacity: sel ? 1 : .5 }}>
                    <NI.Settings/>
                  </button>
                </div>
                {pairedSubs.map((sg: any, sgIdx: number) => (
                  <GroupNode key={sg.OID} group={sg} groupRef={null} depth={depth + 1}
                    collapsed={collapsed}
                    selectedOid={selectedOid}
                    searchTerm={searchTerm} matchItem={matchItem}
                    drag={drag}
                    sectionDrag={sectionDrag}
                    formOid={group.OID}
                    ownIdx={sgIdx}/>
                ))}
              </Fragment>
            );
          })}

          <div onDragOver={onDragOver(group.OID, group.ItemRef.length)} onDrop={onDrop(group.OID, group.ItemRef.length)}
            onClick={() => onAddQuestion(group.OID)} className="nm-dashed"
            style={{ margin: `3px 8px 4px ${indent + 22}px`, padding: '5px 10px', fontSize: 11.5,
              color: NT.ink3, display: 'flex', alignItems: 'center', gap: 6 }}>
            <NI.Plus/> Add question
          </div>

          {unpairedSubgroups.map((sg: any, sgIdx: number) => (
            <GroupNode key={sg.OID} group={sg} groupRef={null} depth={depth + 1}
              collapsed={collapsed}
              selectedOid={selectedOid}
              searchTerm={searchTerm} matchItem={matchItem}
              drag={drag}
              sectionDrag={sectionDrag}
              formOid={group.OID}
              ownIdx={sgIdx}/>
          ))}

          {depth < 3 && (
            <div onClick={() => onAddSubgroup(group.OID)}
              style={{ margin: `2px 8px 6px ${indent + 22}px`, padding: '4px 10px', fontSize: 11,
                color: NT.ink4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                borderRadius: 4, fontWeight: 500 }}
              onMouseEnter={e => e.currentTarget.style.color = NT.primary}
              onMouseLeave={e => e.currentTarget.style.color = NT.ink4}>
              <NI.Group/> Add subgroup
            </div>
          )}
        </div>
      )}
    </div>
  );
}

Object.assign(window, { OutlineTree });
export { OutlineTree };
